import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { type TranslationSchema } from '~/locales/resources'

// 9segs
export const nineNumbers = [1, 2, 3, 4, 5, 6, 7, 8, 9] as const
export type NineNumber = (typeof nineNumbers)[number]

export const nineSegments = [
  'nineSegment1',
  'nineSegment2',
  'nineSegment3',
  'nineSegment4',
  'nineSegment5',
  'nineSegment6',
  'nineSegment7',
  'nineSegment8',
  'nineSegment9',
] as const
export type NineSegment = (typeof nineSegments)[number]

export const reversedNineSegments = R.reverse(nineSegments)

export const nineSegmentAttributeI18nKeyMap = {
  nineSegment1: 'loyal_customers_1',
  nineSegment2: 'loyal_customers_2',
  nineSegment3: 'regular_customers',
  nineSegment4: 'regular_customers_1',
  nineSegment5: 'defected_customers',
  nineSegment6: 'defected_customers_1',
  nineSegment7: 'aware_noncustomers',
  nineSegment8: 'aware_noncustomers_1',
  nineSegment9: 'unaware',
} as const satisfies Record<NineSegment, keyof TranslationSchema>

export const nineSegmentIntentionI18nKeyMap = {
  nineSegment1: 'active',
  nineSegment2: 'passive_1',
  nineSegment3: 'active_1',
  nineSegment4: 'passive_2',
  nineSegment5: 'active_2',
  nineSegment6: 'passive_3',
  nineSegment7: 'active_3',
  nineSegment8: 'passive_4',
  nineSegment9: '',
} as const satisfies Record<NineSegment, keyof TranslationSchema>

export const nineSegmentNumberWithIntentionI18nKeyMap = {
  nineSegment1: '1_active',
  nineSegment2: '2_passive',
  nineSegment3: '3_active',
  nineSegment4: '4_passive',
  nineSegment5: '5_active',
  nineSegment6: '6_passive',
  nineSegment7: '7_active',
  nineSegment8: '8_passive',
  nineSegment9: '9_unaware',
} as const satisfies Record<NineSegment, keyof TranslationSchema>

export type NineSegmentCounts = Record<NineSegment, number>

export const nineSegmentIndicators = [
  // NPI
  'npi',
  // a-NPI
  'aNpi',
  // n-NPI
  'nNpi',
  // u-NPI
  'uNpi',
] as const
export type NineSegmentIndicator = (typeof nineSegmentIndicators)[number]

export const nineSegmentIndicatorLabelMap = {
  npi: 'NPI',
  aNpi: 'a-NPI',
  nNpi: 'n-NPI',
  uNpi: 'u-NPI',
} as const satisfies Record<NineSegmentIndicator, string>

// 5segs
export const fiveNumbers = [1, 2, 3, 4, 5] as const
export type FiveNumber = (typeof fiveNumbers)[number]

export const fiveSegments = [
  'fiveSegment1',
  'fiveSegment2',
  'fiveSegment3',
  'fiveSegment4',
  'fiveSegment5',
] as const
export type FiveSegment = (typeof fiveSegments)[number]

export const fiveSegmentLabelI18nKeyMap = {
  fiveSegment1: 'loyal_customers_3',
  fiveSegment2: 'regular_customers_2',
  fiveSegment3: 'defected_customers_2',
  fiveSegment4: 'aware_noncustomers_2',
  fiveSegment5: 'unaware_1',
} as const satisfies Record<FiveSegment, keyof TranslationSchema>

// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const wrapFiveSegmentLabelMapWithTranslate = (
  obj: typeof fiveSegmentLabelI18nKeyMap,
  t: TFunction,
) => {
  const result = {} as Record<keyof typeof obj, string>

  for (const key of Object.keys(obj) as (keyof typeof obj)[]) {
    result[key] = t(obj[key])
  }

  return result
}

export type FiveSegmentCounts = Record<FiveSegment, number>

export const fiveSegmentIndicators = [
  // 認知
  'rateRecognition',
  // 既存顧客
  'rateCurrentUser',
  // ロイヤル顧客
  'rateLoyal',
  // ユーザー転換率
  'rateCv',
  // ロイヤル率
  'ratioLoyalToCurrentUser',
] as const
export type FiveSegmentIndicator = (typeof fiveSegmentIndicators)[number]

export const fiveSegmentIndicatorLabelI18nKeyMap = {
  rateRecognition: 'aware_1',
  rateCurrentUser: 'existing_customer_1',
  rateLoyal: 'loyal_customers_4',
  rateCv: 'user_conversion_rateamong_aware_1',
  ratioLoyalToCurrentUser: 'loyal_customersty_rateamong_existing_customers_1',
} as const satisfies Record<FiveSegmentIndicator, keyof TranslationSchema>

// eslint-disable-next-line jsdoc/require-param, jsdoc/require-returns
/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
export const wrapFiveSegmentIndicatorLabelWithTranslate = (
  obj: typeof fiveSegmentIndicatorLabelI18nKeyMap,
  t: TFunction,
) => {
  const result = {} as Record<keyof typeof obj, string>

  for (const key of Object.keys(obj) as (keyof typeof obj)[]) {
    result[key] = t(obj[key])
  }

  return result
}

// 9segs <=> 5segs
export const fiveToNineNumbersMap = {
  1: [1, 2],
  2: [3, 4],
  3: [5, 6],
  4: [7, 8],
  5: [9],
} as const satisfies Record<FiveNumber, readonly NineNumber[]>

export const fiveToNineSegmentsMap = {
  fiveSegment1: ['nineSegment1', 'nineSegment2'],
  fiveSegment2: ['nineSegment3', 'nineSegment4'],
  fiveSegment3: ['nineSegment5', 'nineSegment6'],
  fiveSegment4: ['nineSegment7', 'nineSegment8'],
  fiveSegment5: ['nineSegment9'],
} as const satisfies Record<FiveSegment, readonly NineSegment[]>
